import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isRealBirthDate, isUnder14, madridToday, toIsoDate, type DayParts } from "../../lib/consentSession";
import ConsentDialog, {
  DialogError,
  DialogText,
  DialogTitle,
  LINK_BUTTON,
  PRIMARY_BUTTON,
} from "./ConsentDialog";

const SELECT_CLASS =
  "h-10 w-full min-w-0 rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] disabled:opacity-40";

function monthNames(language: string): string[] {
  try {
    const fmt = new Intl.DateTimeFormat(language, { month: "long", timeZone: "UTC" });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(Date.UTC(2000, i, 1))));
  } catch {
    return Array.from({ length: 12 }, (_, i) => String(i + 1));
  }
}

/**
 * Date of birth (Figma "Web / Privacy / Date of birth" 4445:4771 / 4445:4801, Dark 4446:4782 / 4446:4809): Day /
 * Month / Year selects, "Why we ask", Continue once a real date is chosen. The date lives only in this component's
 * state and goes to `onContinue` (with the 14+ check on Spain's date); it is never stored or logged in the browser.
 */
export default function DateOfBirthDialog({
  onContinue,
  busy = false,
  error = null,
  privacyUrl,
  onSignOut,
}: {
  onContinue: (birthDate: string, under14: boolean) => void;
  busy?: boolean;
  error?: string | null;
  privacyUrl?: string;
  /** Existing user: a way out besides answering. */
  onSignOut?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [why, setWhy] = useState(false);
  const today = useMemo(() => madridToday(), []);
  const months = useMemo(() => monthNames(i18n.language), [i18n.language]);
  const years = useMemo(
    () => Array.from({ length: today.year - 1900 + 1 }, (_, i) => today.year - i),
    [today.year],
  );

  const parts: DayParts | null =
    day && month && year ? { day: Number(day), month: Number(month), year: Number(year) } : null;
  const real = parts !== null && isRealBirthDate(parts, today);
  const invalid = parts !== null && !real;

  return (
    <ConsentDialog testId="consent-dob" title={t("dob_title")}>
      <DialogTitle>{t("dob_title")}</DialogTitle>
      <DialogText>{t("dob_text")}</DialogText>
      <fieldset className="mt-4 border-none p-0" disabled={busy}>
        <legend className="mb-1.5 p-0 text-sm font-semibold text-[var(--foreground)]">{t("dob_title")}</legend>
        <div className="flex gap-2">
          <select
            aria-label={t("dob_day")}
            data-testid="consent-dob-day"
            className={SELECT_CLASS}
            value={day}
            onChange={(e) => setDay(e.target.value)}
          >
            <option value="">{t("dob_day")}</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            aria-label={t("dob_month")}
            data-testid="consent-dob-month"
            className={SELECT_CLASS}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            <option value="">{t("dob_month")}</option>
            {months.map((name, i) => (
              <option key={i} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label={t("dob_year")}
            data-testid="consent-dob-year"
            className={SELECT_CLASS}
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            <option value="">{t("dob_year")}</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </fieldset>
      {invalid && <DialogError testId="consent-dob-invalid">{t("dob_invalid")}</DialogError>}
      <button
        type="button"
        data-testid="consent-dob-why"
        aria-expanded={why}
        className={`${LINK_BUTTON} mt-3 no-underline`}
        onClick={() => setWhy((v) => !v)}
      >
        {t("dob_why")}
      </button>
      {why && <DialogText>{t("dob_why_body")}</DialogText>}
      {error && <DialogError testId="consent-dob-error">{error}</DialogError>}
      <div className="mt-4 flex">
        <button
          type="button"
          data-testid="consent-dob-continue"
          className={PRIMARY_BUTTON}
          disabled={!real || busy}
          onClick={() => parts && onContinue(toIsoDate(parts), isUnder14(parts, today))}
        >
          {t("dob_continue")}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--foreground-muted)]">
        {privacyUrl ? (
          <button
            type="button"
            data-testid="consent-dob-privacy"
            className={`${LINK_BUTTON} text-xs font-normal`}
            onClick={() => window.open(privacyUrl, "_blank", "noopener,noreferrer")}
          >
            {t("consent_read_privacy_notice")}
          </button>
        ) : (
          <span />
        )}
        {onSignOut && (
          <button
            type="button"
            data-testid="consent-dob-sign-out"
            className={`${LINK_BUTTON} text-xs`}
            disabled={busy}
            onClick={onSignOut}
          >
            {t("logout_label")}
          </button>
        )}
      </div>
    </ConsentDialog>
  );
}

/** Under 14 (Figma "Web / Privacy / Under 14" 4445:4921, Dark 4446:4863): no account, nothing stored. */
export function Under14Dialog({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <ConsentDialog testId="consent-under14" title={t("under14_title")}>
      <div
        aria-hidden="true"
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--primary-soft)] text-[var(--primary-text)]"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 21h16M5 21v-7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7M5 16c2 1 3-1 5 0s3 1 5 0 3-1 4 0M12 12V8M12 5v.01" />
        </svg>
      </div>
      <DialogTitle>{t("under14_title")}</DialogTitle>
      <DialogText>{t("under14_text")}</DialogText>
      <div className="mt-4 flex">
        <button
          type="button"
          data-testid="consent-under14-back"
          className="h-10 flex-1 rounded-full border border-[var(--border)] bg-[var(--bg-surface)] px-4 text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--bg-hover)]"
          onClick={onBack}
        >
          {t("under14_back")}
        </button>
      </div>
    </ConsentDialog>
  );
}
